import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef
} from 'react';
import { Link } from 'react-router-dom';
import {
  Grid,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { SgAccessContext } from 'src/components/manage-users/SgAcessProvider';
import { copyToClipBoard } from 'src/util/util';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { createUniqueLink } from 'src/components/generate-link/TableActions';
import { validatePedigreeConfig } from 'src/components/pedigree-chart/pedigree-tool/pedigree/utils';

const StatusChip = ({ label }) => {
  const colors =
    CONSTANTS.fhqStatusColors[label] ||
    CONSTANTS.fhqStatusColors[CONSTANTS.fhqStatus.NOT_SENT];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        px: 1.25,
        borderRadius: '100px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
        bgcolor: colors.bg,
        color: colors.fg
      }}
    >
      {label}
    </Box>
  );
};
const FamilyHistoryMenu = ({ user, active, refreshKey }) => {
  const t = useAutoTranslation();
  const probandId = user?._id || user?.user_id;
  const { setSnack } = useContext(SnackContext);
  const { allow, readOnly: roFn } = useContext(SgAccessContext);
  const fhqAllowed = allow('fhqs');
  const fhqReadOnly = roFn('fhqs');
  const canSend = fhqAllowed && !fhqReadOnly;

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const autoAppliedRef = useRef(false);
  const [autoApplyFailed, setAutoApplyFailed] = useState(false);

  const fetchStatus = useCallback(() => {
    if (!probandId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(APICONSTANTS.getProbandFhqStatus.replace(':probandId', probandId))
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [probandId]);

  useEffect(() => {
    if (active === undefined || active) fetchStatus();
  }, [active, fetchStatus]);

  useEffect(() => {
    if (refreshKey) fetchStatus();
  }, [refreshKey, fetchStatus]);

  const issue = (withEmail) => {
    setBusy(true);
    axios
      .post(APICONSTANTS.issueFhqForProband, { user_id: probandId, withEmail })
      .then((res) => {
        const link = res.data?.token
          ? createUniqueLink(res.data.token)
          : res.data?.link || '';
        setGeneratedLink(link);
        setSnack({
          status: true,
          msg: withEmail
            ? t('Questionnaire sent')
            : t('Link generated successfully'),
          severity: 'success'
        });
        fetchStatus();
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg: err?.response?.data?.error || t('An error has occurred!'),
          severity: 'error'
        })
      )
      .finally(() => setBusy(false));
  };

  const computeNodes = async (familyId) => {
    if (!familyId) return [];
    const resp = await axios.get(
      APICONSTANTS.getPedigreeDetails.replace(':familyId', familyId)
    );
    const { proband, families } = resp.data || {};
    if (!proband) return [];
    const ordered =
      validatePedigreeConfig({ user: proband, members: families || [] }) || [];
    const probandKey = String(proband._id || proband.pid || proband.uid);
    return ordered.map((n) => ({
      pid: String(n.pid),
      uid: n.uid != null ? String(n.uid) : String(n.pid),
      isDummy: !!n.isDummy,
      gender: n.gender || null,
      isProband: String(n.pid) === probandKey,
      fatherId: n.fatherId != null ? String(n.fatherId) : null,
      motherId: n.motherId != null ? String(n.motherId) : null,
      spouseIds: (n.spouseIds || []).map(String),
      childrenIds: (n.childrenIds || []).map(String)
    }));
  };

  const apply = async () => {
    setBusy(true);
    try {
      let fhqNodes = [];
      let realNodes = [];
      try {
        fhqNodes = await computeNodes(status?.shadowFamilyId);
      } catch (_) {
        fhqNodes = [];
      }
      try {
        realNodes = await computeNodes(status?.realFamilyId || user?.familyId);
      } catch (_) {
        realNodes = [];
      }

      const res = await axios.post(
        APICONSTANTS.applyFhq.replace(':probandId', probandId),
        { fhqNodes, realNodes }
      );
      const added = res.data?.created ?? 0;
      const merged = res.data?.updated ?? 0;
      const changed = added > 0 || merged > 0;
      setSnack({
        status: true,
        msg: changed
          ? t(
              `Applied to the pedigree (${added} added, ${merged} updated). Refreshing…`
            )
          : t('No new questionnaire data to merge into the pedigree.'),
        severity: changed ? 'success' : 'info'
      });
      fetchStatus();
      if (changed) setTimeout(() => window.location.reload(), 1200);
      return true;
    } catch (err) {
      setSnack({
        status: true,
        msg: err?.response?.data?.error || t('An error has occurred!'),
        severity: 'error'
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    autoAppliedRef.current = false;
    setAutoApplyFailed(false);
  }, [probandId]);
  useEffect(() => {
    if (
      status?.autoApply &&
      canSend &&
      !autoAppliedRef.current &&
      !busy &&
      !autoApplyFailed
    ) {
      autoAppliedRef.current = true;
      apply().then((ok) => {
        if (!ok) setAutoApplyFailed(true);
      });
    }
  }, [status, canSend]);

  const discard = () => {
    setBusy(true);
    axios
      .post(APICONSTANTS.discardFhq.replace(':probandId', probandId))
      .then(() => {
        setSnack({
          status: true,
          msg: t('Submission discarded'),
          severity: 'success'
        });
        setGeneratedLink('');
        fetchStatus();
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg: err?.response?.data?.error || t('An error has occurred!'),
          severity: 'error'
        })
      )
      .finally(() => setBusy(false));
  };

  const copy = () => {
    copyToClipBoard(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  if (!probandId) return null;

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!status) {
    return (
      <Grid sx={{ p: 1 }}>
        <Link
          to={`/admin/users/view/${probandId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <Button variant="outlined">{t('View Family History')}</Button>
        </Link>
      </Grid>
    );
  }

  if (status.createdFromFhq) {
    return (
      <Grid sx={{ p: 1 }}>
        <Link
          to={`/admin/users/view/${probandId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <Button variant="outlined">{t('View Family History')}</Button>
        </Link>
      </Grid>
    );
  }

  const label = status.fhqStatus || CONSTANTS.fhqStatus.NOT_SENT;
  const hasEmail = !!status.hasEmail || !!user?.email;
  const isNotSent = label === CONSTANTS.fhqStatus.NOT_SENT;
  const isSent = label === CONSTANTS.fhqStatus.SENT;
  const isSubmitted = label === CONSTANTS.fhqStatus.SUBMITTED;
  const canView = !!status.canViewFamilyHistory && status.shadowProbandId;

  return (
    <Grid container direction="column" spacing={1.5} sx={{ p: 1 }}>
      <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('FHQ Status')}:
        </Typography>
        <StatusChip label={label} />
      </Grid>

      {canView && (
        <Grid>
          <Link
            to={`/admin/users/view/${status.shadowProbandId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Button variant="outlined" fullWidth>
              {t('View Family History')}
            </Button>
          </Link>
        </Grid>
      )}
      {isSubmitted &&
        canSend &&
        (status.autoApply && !autoApplyFailed ? (
          <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {t('Applying the questionnaire to the pedigree…')}
            </Typography>
          </Grid>
        ) : (
          <>
            <Grid>
              <Alert severity="info">
                {t(
                  'The patient has submitted their questionnaire. Review it above, then Apply to merge it into the pedigree, or Discard to remove it.'
                )}
              </Alert>
            </Grid>
            <Grid sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                disabled={busy}
                onClick={apply}
                startIcon={busy ? <CircularProgress size={14} /> : null}
              >
                {t('Apply')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={busy}
                onClick={discard}
              >
                {t('Discard')}
              </Button>
            </Grid>
          </>
        ))}

      {(isNotSent || isSent) && (
        <>
          {isSent && (
            <Grid>
              <Alert severity="info">
                {t(
                  'A questionnaire link has been sent and is awaiting the patient’s response. You can resend it or generate a fresh link below.'
                )}
              </Alert>
            </Grid>
          )}
          {canSend ? (
            <>
              {!hasEmail && (
                <Grid>
                  <Alert severity="info">
                    {t(
                      'Select the proband, add an email in the Person section and save to generate link.Reference'
                    )}
                  </Alert>
                </Grid>
              )}
              <Grid sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={
                    busy ? <CircularProgress size={14} /> : <EmailIcon />
                  }
                  disabled={busy || !hasEmail}
                  onClick={() => issue(true)}
                >
                  {isSent ? t('Resend link') : t('Generate and send link')}
                </Button>
              </Grid>
            </>
          ) : (
            fhqAllowed && (
              <Grid>
                <Typography variant="caption" sx={{ color: '#6b7a8d' }}>
                  {t('Read-only: you cannot send questionnaires.')}
                </Typography>
              </Grid>
            )
          )}
          {generatedLink && (
            <Grid sx={{ minWidth: 0 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                value={generatedLink}
                title={generatedLink}
                slotProps={{
                  input: {
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: 12, pr: 0.5 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? t('Copied!') : t('Copy link')}>
                          <IconButton size="small" onClick={copy} edge="end">
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

export default FamilyHistoryMenu;
